import React, { useState, useEffect } from 'react';
import ColorPicker from '../components/ColorPicker';
import CustomFontPicker from '../dashboard/fontPicker';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { generateCustomTheme } from './stylePresets';

const CustomStyleCreator = ({ 
  isExpanded, 
  onToggle, 
  onSave, 
  gameType, 
  title, 
  description, 
  initialColors = null,
  initialFont = null,
  editingStyleId = null
}) => {
  const [customColors, setCustomColors] = useState({
    // Main theme colors based on retro structure
    background: '#2C3E50',      // Dark base for backgrounds
    surface: '#34495E',         // Slightly lighter for cards/surfaces  
    primary: '#F1C40F',         // Bright accent for titles/highlights
    action: '#E74C3C',          // Strong color for buttons/actions
    text: '#ECF0F1'             // Light text on dark backgrounds
  });
  const [customFont, setCustomFont] = useState('Share Tech Mono');
  const [activeColorPicker, setActiveColorPicker] = useState(null);

  // Update colors and font when initial values change
  useEffect(() => {
    if (initialColors) {
      setCustomColors(initialColors);
    }
    if (initialFont) {
      setCustomFont(initialFont);
    }
  }, [initialColors, initialFont]);

  // Generate preview theme whenever colors or font change
  const previewTheme = generateCustomTheme(customColors, customFont, gameType);

  const handleColorChange = (colorKey, newColor) => {
    setCustomColors(prev => ({
      ...prev,
      [colorKey]: newColor
    }));
  };

  const handleSave = () => {
    const customTheme = generateCustomTheme(customColors, customFont, gameType);
    const saveData = {
      theme: customTheme,
      colors: customColors,
      font: customFont,
      editingStyleId
    };
    onSave(saveData);
  };

  const getBackgroundStyle = (theme) => {
    const colors = theme?.colors || {};
    const gradientStart = colors?.background?.gradient?.start || '#F3F4F6';
    const gradientEnd = colors?.background?.gradient?.end || '#E5E7EB';
    
    return {
      background: `linear-gradient(to bottom, ${gradientStart}, ${gradientEnd})`
    };
  };

  // Hex to RGB conversion for opacity
  const hexToRgb = (hex) => {
    try {
      if (!hex || typeof hex !== 'string') return '0, 0, 0';
      hex = hex.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return isNaN(r) || isNaN(g) || isNaN(b) ? '0, 0, 0' : `${r}, ${g}, ${b}`;
    } catch (error) {
      console.error('Error in hexToRgb:', error);
      return '0, 0, 0';
    }
  };

  const colorSections = [
    {
      title: 'Background Colors',
      description: 'Base colors for backgrounds and gradients',
      colors: [
        { key: 'background', label: 'Background Base', description: 'Main background color' },
        { key: 'surface', label: 'Surface/Cards', description: 'Cards and elevated surfaces' }
      ]
    },
    {
      title: 'Content Colors', 
      description: 'Colors for text and primary content',
      colors: [
        { key: 'primary', label: 'Primary Text', description: 'Titles and important text' },
        { key: 'text', label: 'Secondary Text', description: 'Body text and subtitles' }
      ]
    },
    {
      title: 'Interactive Colors',
      description: 'Colors for buttons and interactive elements', 
      colors: [
        { key: 'action', label: 'Action/Buttons', description: 'Primary buttons and actions' }
      ]
    }
  ];

  if (!isExpanded) return null;

  return (
    <div className="rounded-xl p-6 mt-6" style={{
      background: 'linear-gradient(135deg, #2C3E50, #34495E)',
      border: '2px solid rgba(241, 196, 15, 0.4)',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
    }}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Color Customization */}
        <div className="lg:col-span-2 space-y-6">
          <div>
            <h3 className="text-lg font-bold tracking-wide mb-2" style={{
              color: '#F1C40F',
              fontFamily: '"Share Tech Mono", monospace',
              textShadow: '0 0 8px rgba(241, 196, 15, 0.3)'
            }}>CUSTOMIZE YOUR STYLE</h3>
            <p className="text-sm font-bold tracking-wide mb-6" style={{
              color: '#95A5A6',
              fontFamily: '"Share Tech Mono", monospace'
            }}>Choose colors that work well together. Think about contrast and readability.</p>
          </div>

          {/* Color Sections */}
          {colorSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="space-y-4">
              <div>
                <h4 className="text-md font-bold tracking-wide" style={{
                  color: '#ECF0F1',
                  fontFamily: '"Share Tech Mono", monospace'
                }}>{section.title.toUpperCase()}</h4>
                <p className="text-xs font-bold tracking-wide" style={{
                  color: '#95A5A6',
                  fontFamily: '"Share Tech Mono", monospace'
                }}>{section.description}</p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {section.colors.map((colorDef) => (
                  <div key={colorDef.key} className="space-y-2">
                    <ColorPicker
                      label={colorDef.label}
                      color={customColors[colorDef.key]}
                      onChange={(color) => handleColorChange(colorDef.key, color)}
                      isActive={activeColorPicker === colorDef.key}
                      onToggle={() => setActiveColorPicker(activeColorPicker === colorDef.key ? null : colorDef.key)}
                    />
                    <p className="text-xs font-bold tracking-wide ml-11" style={{
                      color: '#95A5A6',
                      fontFamily: '"Share Tech Mono", monospace'
                    }}>{colorDef.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Font Selection */}
          <div className="space-y-4">
            <div>
              <h4 className="text-md font-bold tracking-wide" style={{
                color: '#ECF0F1',
                fontFamily: '"Share Tech Mono", monospace'
              }}>TYPOGRAPHY</h4>
              <p className="text-xs font-bold tracking-wide" style={{
                color: '#95A5A6',
                fontFamily: '"Share Tech Mono", monospace'
              }}>Choose a font that matches your style</p>
            </div>
            <CustomFontPicker
              label="Primary Font"
              value={customFont}
              onChange={setCustomFont}
            />
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-3 lg:space-y-4">
          <div>
            <h3 className="text-base lg:text-lg font-bold tracking-wide" style={{
              color: '#F1C40F',
              fontFamily: '"Share Tech Mono", monospace',
              textShadow: '0 0 8px rgba(241, 196, 15, 0.3)'
            }}>LIVE PREVIEW</h3>
            <p className="text-xs lg:text-sm font-bold tracking-wide" style={{
              color: '#95A5A6',
              fontFamily: '"Share Tech Mono", monospace'
            }}>See how your colors work together</p>
          </div>
          
          <div className="relative rounded-lg overflow-hidden border-2" style={{
            borderColor: 'rgba(241, 196, 15, 0.4)'
          }}>
            <div className="aspect-[4/3] lg:aspect-[3/4] relative" style={getBackgroundStyle(previewTheme)}>
              <div className="absolute inset-0 flex items-center justify-center">
                <div 
                  className="w-[80%] max-w-[200px] lg:w-[85%] lg:max-w-[250px] rounded shadow-xl" 
                  style={{ 
                    backgroundColor: previewTheme.colors?.cardBackground ? 
                      `rgba(${hexToRgb(previewTheme.colors.cardBackground)}, ${previewTheme.colors.cardOpacity || 1})` : 
                      'rgba(255, 255, 255, 0.9)',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  }}
                >
                  <div className="relative p-2 lg:p-3">
                    <div>
                      <h3 
                        style={{ 
                          color: previewTheme.colors?.titleText,
                          fontFamily: previewTheme.typography?.primaryFont || 'Inter',
                          fontWeight: '600'
                        }} 
                        className="text-xs lg:text-sm font-semibold tracking-tight truncate"
                      >
                        {title || 'Example Title'}
                      </h3>
                      <p 
                        style={{ 
                          color: previewTheme.colors?.subtitleText,
                          fontFamily: previewTheme.typography?.primaryFont || 'Inter',
                          fontWeight: '400'
                        }} 
                        className="text-[10px] lg:text-xs mt-1 opacity-90 truncate"
                      >
                        {description || 'Example description text'}
                      </p>
                      <button
                        className="w-full mt-2 lg:mt-3 px-2 lg:px-3 py-1 lg:py-2 rounded text-[10px] lg:text-xs shadow-sm transition-all duration-200 font-semibold"
                        style={{ 
                          backgroundColor: previewTheme.colors?.buttonBackground,
                          color: previewTheme.colors?.buttonText || '#FFFFFF',
                          fontFamily: previewTheme.typography?.primaryFont || 'Inter',
                          border: previewTheme.colors?.buttonBorder ? `1px solid ${previewTheme.colors.buttonBorder}` : 'none'
                        }}
                      >
                        Start Game
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Color relationships info */}
          <div className="text-[10px] lg:text-xs space-y-1 p-2 lg:p-3 rounded border" style={{
            backgroundColor: 'rgba(52, 73, 94, 0.6)',
            border: '1px solid rgba(241, 196, 15, 0.4)',
            color: '#95A5A6',
            fontFamily: '"Share Tech Mono", monospace'
          }}>
            <p className="font-bold tracking-wide"><span style={{color: '#F1C40F'}}>BACKGROUND:</span> Base gradient and overall feel</p>
            <p className="font-bold tracking-wide"><span style={{color: '#F1C40F'}}>SURFACE:</span> Cards and content areas</p>
            <p className="font-bold tracking-wide"><span style={{color: '#F1C40F'}}>PRIMARY:</span> Titles and important elements</p>
            <p className="font-bold tracking-wide"><span style={{color: '#F1C40F'}}>ACTION:</span> Buttons and interactive elements</p>
            <p className="font-bold tracking-wide"><span style={{color: '#F1C40F'}}>TEXT:</span> Readable body text</p>
          </div>

          {/* Actions */}
          <div className="flex flex-col space-y-2 lg:space-y-3">
            <button
              onClick={handleSave}
              className="w-full px-3 lg:px-4 py-2 lg:py-3 rounded-lg transition-all duration-200 text-xs lg:text-sm font-bold tracking-wide"
              style={{
                background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                color: '#FFFFFF',
                fontFamily: '"Share Tech Mono", monospace',
                border: '2px solid rgba(241, 196, 15, 0.4)',
                boxShadow: '0 4px 15px rgba(231, 76, 60, 0.3)',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(231, 76, 60, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(231, 76, 60, 0.3)';
              }}
            >
              {editingStyleId ? 'UPDATE CUSTOM STYLE' : 'APPLY CUSTOM STYLE'}
            </button>
            <button
              onClick={onToggle}
              className="w-full px-3 lg:px-4 py-1 lg:py-2 rounded-lg transition-all duration-200 text-xs lg:text-sm font-bold tracking-wide"
              style={{
                backgroundColor: 'rgba(52, 73, 94, 0.6)',
                color: '#95A5A6',
                fontFamily: '"Share Tech Mono", monospace',
                border: '1px solid rgba(149, 165, 166, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(52, 73, 94, 0.8)';
                e.target.style.color = '#ECF0F1';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(52, 73, 94, 0.6)';
                e.target.style.color = '#95A5A6';
              }}
            >
              CANCEL
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomStyleCreator; 